import { useCallback, useEffect, useRef, useState } from "react";
import IUserUI from "../../models/IUserUI";
import ISupplierModel from "../../models/ISupplierModel";
import IVendorModel from "../../models/IVendorModel";
import IActiveLoginMessage from "../../models/IActiveLoginMessage";
import { acknowledgeLoginMessage, getActiveLoginMessage, LoginMessageSupersededError } from "../../services/LoginMessageService";

export interface UseLoginMessageAcknowledgmentOptions {
    /** Only ecommerce portal vendor users are gated; App.tsx passes false for everyone else. */
    enabled: boolean;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    vendorModel?: IVendorModel;
}

export interface UseLoginMessageAcknowledgmentResult {
    /** The message the user must acknowledge before using the portal, or null when nothing is pending. */
    pendingMessage: IActiveLoginMessage | null;
    /** The supplier's active message as last fetched (required or not), or null. */
    activeMessage: IActiveLoginMessage | null;
    /** True once the user has acknowledged the active message (lets the portal show it again, collapsed). */
    acknowledged: boolean;
    acknowledging: boolean;
    error: string | null;
    acknowledge: () => Promise<void>;
    refresh: () => Promise<void>;
}

/**
 * Fetches the supplier's active login message for the signed-in user and exposes the one that still
 * needs an "I understand". Re-fetches when the supplier model's mirrored message changes (an admin
 * edit arrives over the socket), so a republished message re-prompts while the user is online.
 *
 * Takes the models as arguments rather than reading contexts: App.tsx is the context PROVIDER, and
 * that is where the blocking modal must live so it covers every /ecomm route.
 *
 * A failed fetch fails OPEN (no modal): a network blip must not lock a customer out of the portal.
 */
export const useLoginMessageAcknowledgment = (options: UseLoginMessageAcknowledgmentOptions): UseLoginMessageAcknowledgmentResult => {
    const { enabled, userModel, supplierModel, vendorModel } = options;

    const [message, setMessage] = useState<IActiveLoginMessage | null>(null);
    const [acknowledging, setAcknowledging] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Latest models for the async callbacks without re-creating them on every App render.
    const modelsRef = useRef({ userModel, supplierModel, vendorModel });
    modelsRef.current = { userModel, supplierModel, vendorModel };

    // Guards against an older in-flight response overwriting a newer one.
    const requestIdRef = useRef(0);

    const userGuid = userModel?.guid;
    const userToken = userModel?.token;
    const supplierGuid = supplierModel?.guid;
    const vendorGuid = vendorModel?.guid;
    const mirroredGuid = supplierModel?.login_message_detail?.guid;
    const mirroredRequiresAck = supplierModel?.login_message_detail?.requires_acknowledgment;

    const refresh = useCallback(async () => {
        const requestId = ++requestIdRef.current;

        if (!enabled || !userGuid || !userToken || !supplierGuid) {
            setMessage(null);
            return;
        }

        try {
            const current = modelsRef.current;
            const result = await getActiveLoginMessage(current.userModel, current.supplierModel, current.vendorModel);

            if (requestId === requestIdRef.current) {
                setMessage(result);
                setError(null);
            }
        } catch (err) {
            console.error("useLoginMessageAcknowledgment: failed to load the active login message", err);

            if (requestId === requestIdRef.current) {
                setMessage(null);
            }
        }
    }, [enabled, userGuid, userToken, supplierGuid]);

    // vendorGuid is read through modelsRef; listing it here re-fetches when the user switches accounts.
    useEffect(() => {
        refresh();
    }, [refresh, vendorGuid, mirroredGuid, mirroredRequiresAck]);

    const acknowledge = useCallback(async () => {
        if (!message) {
            return;
        }

        setAcknowledging(true);
        setError(null);

        try {
            const current = modelsRef.current;
            await acknowledgeLoginMessage(message.guid, current.userModel, current.supplierModel, current.vendorModel);

            setMessage((previous) => (previous && previous.guid === message.guid ? { ...previous, acknowledged: true } : previous));
        } catch (err) {
            if (err instanceof LoginMessageSupersededError) {
                // The admin changed the text while this was open: show the replacement instead.
                await refresh();
            } else {
                setError(err instanceof Error ? err.message : String(err));
            }
        } finally {
            setAcknowledging(false);
        }
    }, [message, refresh]);

    const pendingMessage = message && message.requires_acknowledgment && !message.acknowledged ? message : null;
    const acknowledged = !!message?.acknowledged;

    return { pendingMessage, activeMessage: message, acknowledged, acknowledging, error, acknowledge, refresh };
};

export default useLoginMessageAcknowledgment;
